import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { useEffect } from "react";
import { session } from "./lib/session";
import LocationPermissionGate from "./components/feature/LocationPermissionGate";
import SupportChatUnreadBubble from "./components/feature/SupportChatUnreadBubble";
import CouponPackagePopup from "./components/feature/CouponPackagePopup";
import CustomerNotifyProvider from "./components/notify/CustomerNotifyProvider";

const findScrollableParent = (target: EventTarget | null, root: HTMLElement) => {
  let node = target instanceof HTMLElement ? target : null;
  while (node && node !== root && node !== document.body) {
    const style = window.getComputedStyle(node);
    const canScrollY = /(auto|scroll)/.test(style.overflowY);
    if (canScrollY && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return root;
};

function App() {
  useEffect(() => {
    session.bootstrap().catch(() => {
      // Ignore bootstrap failures and let pages handle unauthenticated flows explicitly.
    });
  }, []);

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) {
      return;
    }

    let startY = 0;
    const handleTouchStart = (event: TouchEvent) => {
      startY = event.touches[0]?.clientY ?? 0;
    };
    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        return;
      }
      const currentY = event.touches[0]?.clientY ?? startY;
      const pullingDown = currentY > startY;
      if (!pullingDown) {
        return;
      }
      const scroller = findScrollableParent(event.target, root);
      if (scroller.scrollTop <= 0) {
        event.preventDefault();
      }
    };

    root.addEventListener("touchstart", handleTouchStart, { passive: true });
    root.addEventListener("touchmove", handleTouchMove, { passive: false });
    return () => {
      root.removeEventListener("touchstart", handleTouchStart);
      root.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter basename={__BASE_PATH__}>
        <CustomerNotifyProvider>
          <LocationPermissionGate />
          <AppRoutes />
          <SupportChatUnreadBubble />
          <CouponPackagePopup />
        </CustomerNotifyProvider>
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
